
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { type Gift, type ProductSilo, type Brand } from '@/utils/types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Gift as GiftIcon, Info } from 'lucide-react';
import TransactionItem from '../transactions/TransactionItem';

interface InventoryActionsProps {
  selectedGift: Gift | null;
  gifts: Gift[];
  brands: Brand[];
  onSelectTransaction: (gift: Gift) => void;
  onBack?: () => void;
}

const InventoryActions: React.FC<InventoryActionsProps> = ({
  selectedGift,
  gifts,
  brands,
  onSelectTransaction,
  onBack
}) => {
  // Group gifts by product silo and then by brand
  const giftsBySilo: Record<ProductSilo, { [brandId: string]: Gift[] }> = {
    restaurants: {},
    beverages: {},
    convenience: {},
    entertainment: {}
  };

  // Organize gifts by product silo and brand
  gifts.forEach(gift => {
    const brand = brands.find(b => b.id === gift.brandId);
    if (brand) {
      if (!giftsBySilo[brand.silo][brand.id]) {
        giftsBySilo[brand.silo][brand.id] = [];
      }
      giftsBySilo[brand.silo][brand.id].push(gift);
    }
  });

  // Calculate the total value for each brand
  const getBrandTotal = (brandGifts: Gift[]): number => {
    return brandGifts.reduce((sum, gift) => sum + gift.amount, 0);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Available Transactions</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Select a transaction to use, exchange, regift or convert your GivGo Bucks
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <CardDescription>
          Select a transaction to view available actions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="min-h-[300px]">
          {gifts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
              <GiftIcon className="h-12 w-12 mb-4 opacity-20" />
              <p>No transactions available in this category</p>
              <p className="text-sm mt-1">Select a brand from your inventory to view transactions</p>
            </div>
          ) : (
            <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
              {Object.entries(giftsBySilo).map(([silo, brandGifts]) => {
                // Skip empty silos
                if (Object.keys(brandGifts).length === 0) return null;
                
                return (
                  <div key={silo} className="space-y-3">
                    <h3 className="font-medium text-sm uppercase tracking-wide text-white bg-blue-500 rounded-md px-3 py-1.5 shadow-sm inline-block">
                      {silo}
                    </h3>
                    <div className="space-y-4 pl-2">
                      {Object.entries(brandGifts).map(([brandId, brandTransactions]) => {
                        const brand = brands.find(b => b.id === brandId);
                        if (!brand || brandTransactions.length === 0) return null;
                        
                        const brandTotal = getBrandTotal(brandTransactions);
                        
                        return (
                          <div key={brandId} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <img 
                                  src={brand.logo} 
                                  alt={`${brand.name} logo`}
                                  className="w-5 h-5 object-contain rounded-full"
                                />
                                <h4 className="font-medium text-sm">{brand.name}</h4>
                              </div>
                              <Badge variant="outline" className="bg-givgo-light text-givgo-purple">
                                ${brandTotal}
                              </Badge>
                            </div>
                            <div className="space-y-2">
                              {brandTransactions.map(gift => (
                                <TransactionItem 
                                  key={gift.id}
                                  gift={gift}
                                  isSelected={selectedGift?.id === gift.id}
                                  onSelect={onSelectTransaction}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InventoryActions;
